<template>
  <div class="whatsapp-wizard">
    <b-alert
      :show="!!errorMessage"
      variant="danger"
      dismissible
      class="waw-alert"
      @dismissed="errorMessage = ''"
    >
      {{ errorMessage }}
    </b-alert>

    <!-- The coarse rail. Absent on the endings, where there is nothing more of this flow to come. -->
    <div v-if="rail.length" class="waw-rail">
      <div
        v-for="(phase, i) in rail"
        :key="phase.key"
        class="waw-rail__item"
        :class="'is-' + phase.state"
      >
        <span class="waw-rail__dot">
          <template v-if="phase.state === 'done'">&#x2713;</template>
          <template v-else>{{ i + 1 }}</template>
        </span>
        <span class="waw-rail__label">{{ phase.label }}</span>
      </div>
    </div>

    <WizardStepFrame
      v-if="currentComponent"
      :step-key="currentKey"
      :title="currentTitle"
      eyebrow="WhatsApp setup"
      :busy="busy"
      :can-continue="canContinue"
      :continue-label="continueLabel"
      :show-back="showBack"
      :show-continue="showContinue"
      :show-refresh="showRefresh"
      @back="onBack"
      @continue="onContinue"
      @refresh="$emit('refresh')"
    >
      <!-- Every screen that DOES something asks the shell to do it. The shell owns axios, the one
           error banner and the one busy flag, so no screen can put a request on the wire that Back
           and Refresh are not disabled for. -->
      <component
        :is="currentComponent"
        v-bind="stepProps"
        @update="onUpdate"
        @navigate="onNavigate"
        @purchase="submitPurchase"
        @search="searchNumbers"
        @handoff="submitHandoff"
        @connect-link="requestConnectLink"
      />
    </WizardStepFrame>
  </div>
</template>

<script>
/**
 * The WhatsApp self-serve wizard shell (My Account → WhatsApp, when `my-status.wizard.enabled`).
 *
 * Renders ONE screen at a time from a step-config array and `<component :is>`, the same shape as
 * the A2P wizard (src/pages/MyAccount/A2P/A2PRegistrationWizard.vue). Two things about it are
 * deliberately NOT like the A2P wizard, and both are load-bearing:
 *
 * ── 1. SERVER STATE DRIVES EVERY SCREEN. NO STEP INDEX IS EVER PERSISTED. ────────────────────────
 * The A2P wizard saves `{step, form}` to localStorage and asks "resume where you left off?". This
 * one does not, and must not. WhatsApp onboarding is a multi-person, multi-day, multi-device flow:
 * the office manager answers the questions, the owner does the Facebook sign-in on their own laptop
 * days later, and the back half finishes unattended overnight. A step index in one browser's
 * localStorage would be wrong for everyone else and stale for its owner. So which screen shows is
 * DERIVED from `my-status` (`deriveWizardStep`), which is re-read on mount and after every POST —
 * making the wizard refresh-safe, resumable from any machine, and correct for whoever opens it next.
 * The `?tab=whatsapp` deep-link therefore resumes for free.
 *
 * `viewStep` is the ONE piece of local navigation state, it lives in memory only, and it is reset to
 * null on every status change — so the server always wins the moment it has something new to say.
 *
 * ── 2. BACK IS A REVIEW. IT NEVER RE-SUBMITS. ────────────────────────────────────────────────────
 * `POST /wa-onboarding/my-triage` accepts exactly one submission per site: it requires
 * `step === 'not_started'` and answers 409 `onboarding_in_progress` for anything later. That is the
 * ruled UX constraint, not an implementation detail — going Back after submitting and pressing
 * Continue again MUST NOT fire the request. `continueFromConfirm` checks `committed` (which is
 * `status.triage` being echoed back by the server) and, when it is set, simply hands the render
 * back to the server state. The triage and confirm screens also render read-only in that mode, so
 * the practice is not invited to make an edit that could never be saved.
 *
 * ── WHAT THIS COMPONENT DOES NOT OWN ─────────────────────────────────────────────────────────────
 * The Meta SDK loader, `FB.login`, the postMessage listener and the signup-callback POST all stay
 * in WhatsAppTab.vue, which owned them before this wizard existed. The connect screen's button
 * emits `connect` up to the tab. Two SDK loaders would mean two script tags racing for `window.FB`
 * and two listeners answering one postMessage.
 */
import axios from 'axios';
import {
  deriveWizardStep,
  previousWizardStep,
  nextReviewStep,
  wizardRailStates,
  isWizardStepValid,
  isByoAckComplete,
  requiresByoAck,
  isUsPurchasePath,
  milestoneStates,
  statusLine,
} from '@/store/whatsappSelfStart';
import { wizardStep, wizardStepComponent } from './wizardSteps';
import {
  parseWizardError, shouldRefreshStatus, NUMBER_SETUP_UNAVAILABLE_CODES, NUMBER_TAKEN_CODES,
} from './wizardErrors';
import { connectLinkUrl } from './wizardActions';
import { getIdToken } from '../firebaseIdToken';
import WizardStepFrame from './WizardStepFrame.vue';
import WizardIntroStep from './steps/WizardIntroStep.vue';
import WizardTriageStep from './steps/WizardTriageStep.vue';
import WizardByoAckStep from './steps/WizardByoAckStep.vue';
import WizardConfirmStep from './steps/WizardConfirmStep.vue';
import WizardNumberStep from './steps/WizardNumberStep.vue';
import WizardHandoffStep from './steps/WizardHandoffStep.vue';
import WizardConnectStep from './steps/WizardConnectStep.vue';
import WizardProgressStep from './steps/WizardProgressStep.vue';
import WizardLiveStep from './steps/WizardLiveStep.vue';
import WizardHandoffPendingStep from './steps/WizardHandoffPendingStep.vue';
import WizardBlockedStep from './steps/WizardBlockedStep.vue';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

/** A fresh, fully-keyed answer draft. Every key the backend's triage contract names is present. */
function emptyDraft() {
  return {
    hasWhatsAppNumber: null,
    numberOnApp: null,
    // Move-or-keep for a number that is live on the WhatsApp app: true = keep it on the app and give
    // Teamio a new number; false = move it (bring-your-own). Set together with numberOnApp by the
    // triage screen's buttons. See requiresByoAck.
    keepsNumberOnApp: null,
    // Deliberately never asked before the my-triage POST — the country gate that decides whether a
    // local number even exists is evaluated server-side DURING that POST. See WizardTriageStep.
    wantsLocalNumber: null,
    byoNumber: null,
    facebookAccess: null,
    displayName: '',
    consentAcknowledged: false,
    // The six BYO acknowledgements the practice ticks on the bring-your-own path, keyed by the
    // server's own item.key (app_stops, history_lost, …). Empty off that path; the shell spreads
    // these into the my-triage answers only when the answers are actually BYO.
    acknowledgements: {},
  };
}

export default {
  name: 'WhatsAppWizard',

  components: {
    WizardStepFrame,
    WizardIntroStep,
    WizardTriageStep,
    WizardByoAckStep,
    WizardConfirmStep,
    WizardNumberStep,
    WizardHandoffStep,
    WizardConnectStep,
    WizardProgressStep,
    WizardLiveStep,
    WizardHandoffPendingStep,
    WizardBlockedStep,
  },

  props: {
    /** The my-status payload. The wizard renders nothing meaningful without it. */
    status: { type: Object, default: null },
    siteId: { type: String, default: '' },
    /** teamio.sites.Name — prefills the display name. Never overrides a saved one. */
    siteName: { type: String, default: '' },
    /** Owned by the tab: a connect is in flight / Meta's SDK has loaded. */
    connecting: { type: Boolean, default: false },
    sdkReady: { type: Boolean, default: false },
  },

  data() {
    return {
      /**
       * Local navigation only, in memory, never persisted. Null means "render what the server
       * says". Set only while the practice is walking the pre-submit screens or reviewing them.
       */
      viewStep: null,
      draft: emptyDraft(),
      saving: false,
      errorMessage: '',
      /**
       * The public sign-in link, once the practice has asked for one. Deliberately NOT cleared by
       * the status watcher: it is valid for its own hour regardless of what the site's step does,
       * and a Refresh that silently blanked a link someone was mid-way through sending would look
       * exactly like a bug.
       */
      connectLink: '',
      /**
       * The server has just refused to provision a number.
       *
       * The number screen used to derive its "we can't do this" panel from the ABSENCE of a price
       * quote, but my-status stopped returning one on 2026-09-02 (Teamio pays for the number, so no
       * price is customer-facing). The browser therefore has no way to know in advance — the only
       * honest signal is the server's own refusal, which is what this records.
       *
       * Cleared on every new attempt and by the status watcher, so it can never outlive the
       * condition it describes and strand a practice on a panel after the problem has cleared.
       */
      numberSetupUnavailable: false,
      /**
       * The number screen's two lists (2026-09-11), both from `my-number-options`: `recommended`
       * (loaded when the screen comes up) and `search` (the practice's last area-code search). Null
       * until loaded. Kept across status refreshes — a Refresh must not blank a list someone is
       * reading — except that a FAILED recommendation (`failed: true`) is retried by one.
       */
      numberOptions: { recommended: null, search: null },
      numberOptionsLoading: false,
      numberSearching: false,
      /** Re-run after a `number_taken`, so the list the practice was reading comes back fresh. */
      lastNumberSearch: '',
    };
  },

  computed: {
    /** The screen the SERVER's state implies. The default, and the reset target. */
    serverStep() {
      return deriveWizardStep(this.status);
    },

    currentKey() {
      return this.viewStep || this.serverStep;
    },

    currentComponent() {
      return wizardStepComponent(this.currentKey);
    },

    currentTitle() {
      const step = wizardStep(this.currentKey);
      return step ? step.label : '';
    },

    /**
     * The answers are saved server-side. From here on, triage/confirm are a READ-ONLY review and
     * Continue submits nothing — my-triage would 409.
     */
    committed() {
      return !!(this.status && this.status.triage);
    },

    /** The saved triage answers, or an empty object. Every branch decision below reads THESE. */
    triage() {
      return (this.status && this.status.triage) || {};
    },

    /**
     * The acknowledgement wording the server hands us to render (`[{ key, text }]`), or [].
     *
     * The wizard NEVER hardcodes these sentences — they are a versioned legal artifact and rendering
     * exactly what my-status returns is what keeps what the practice ticks pinned to the version the
     * backend stores. Empty means the payload did not arrive; the byo_ack screen fails visibly and
     * `byoAckComplete` stays false so Continue is disabled.
     */
    byoAckItems() {
      const wording = this.status && this.status.byoAckWording;
      return (wording && Array.isArray(wording.items)) ? wording.items : [];
    },

    /** Just the keys the server declared, in order — the set the acknowledgement screen must satisfy. */
    byoAckKeys() {
      return this.byoAckItems
        .map((item) => (item && typeof item.key === 'string' ? item.key : ''))
        .filter((key) => key !== '');
    },

    /** Are all server-declared acknowledgement boxes ticked? False on an empty key set (fail-closed). */
    byoAckComplete() {
      return isByoAckComplete(this.draft, this.byoAckKeys);
    },

    /**
     * The acknowledgements to send with my-triage: exactly the server-declared keys that are ticked,
     * each as `true`. Reduced to the declared key set so a stale key from an earlier answer can never
     * reach the wire, and only ever `true` — the backend records `true` on the BYO path and `null`
     * off it, never a `false`.
     */
    acknowledgementsForSubmit() {
      const acks = this.draft.acknowledgements || {};
      const out = {};
      this.byoAckKeys.forEach((key) => {
        if (acks[key] === true) out[key] = true;
      });
      return out;
    },

    /**
     * Which handoff the practice is looking at, decided from the SERVER's answers.
     *
     * `byo` is the one branch with a fact behind it: a number that is live in the WhatsApp app and
     * being MOVED. Everything else that lands on the handoff screen is a local-number conversation —
     * either because triage said so, or because the practice chose it on the number screen (the first
     * place we know their country and whether a local number is sold there). Defaulting to `local`
     * rather than `byo` matters: the reason code is what pages our team, and telling them to go
     * move a number nobody has is a worse wrong answer than telling them to source one.
     *
     * Decided by `requiresByoAck`, the ONE shared "is this BYO?" rule — never a hand-typed copy of it.
     * A practice KEEPING its number on the app that then asks for a local number must page our team
     * as `local`; the old inline two-field test would have sent them to move a number they are keeping.
     */
    handoffVariant() {
      return requiresByoAck(this.triage) ? 'byo' : 'local';
    },

    /**
     * Would `my-purchase-number` actually accept a purchase for this practice?
     *
     * The same mirrored predicate the backend applies (`isUsPurchasePath`), used to decide whether
     * the handoff screen may offer a route back to the purchase screen at all. Without this check
     * a bring-your-own practice could be walked to a screen whose only button answers
     * `not_purchase_path` — the exact mismatch the mirror exists to prevent.
     */
    canBuyUsNumber() {
      return this.committed && isUsPurchasePath(this.status.triage);
    },

    busy() {
      return this.saving || this.connecting;
    },

    canContinue() {
      // connect is gated on Meta's SDK; a committed review has nothing to validate; byo_ack checks
      // the SERVER keys the pure validator can't see; everything else goes through isWizardStepValid.
      if (this.currentKey === 'connect') return this.sdkReady && !this.connecting;
      if (this.committed && ['triage', 'byo_ack', 'confirm'].includes(this.currentKey)) return true;
      if (this.currentKey === 'byo_ack') return this.byoAckComplete;
      return isWizardStepValid(this.currentKey, this.draft);
    },

    rail() {
      return wizardRailStates(this.currentKey);
    },

    showBack() {
      return previousWizardStep(this.currentKey, this.draft) !== null;
    },

    showContinue() {
      return ['intro', 'triage', 'byo_ack', 'confirm', 'connect'].includes(this.currentKey);
    },

    /**
     * Refresh appears wherever the practice is waiting on something that finishes elsewhere —
     * our side, WhatsApp's side, or a person's. Nothing pushes to this tab, so without it the only
     * way forward from those screens is a full page reload.
     */
    showRefresh() {
      return ['number', 'handoff', 'connect', 'progress', 'handoff_pending'].includes(this.currentKey);
    },

    continueLabel() {
      if (this.currentKey === 'intro') return 'Get started';
      if (this.currentKey === 'connect') return 'Connect WhatsApp';
      // Only the FIRST pass through confirm saves anything; afterwards it is a review.
      if (this.currentKey === 'confirm' && !this.committed) return 'Save and continue';
      return 'Continue';
    },

    stepProps() {
      switch (this.currentKey) {
        case 'triage':
        case 'confirm':
          return { answers: this.draft, readOnly: this.committed };
        case 'byo_ack':
          return {
            // Rendered straight from the server payload — the wizard never hardcodes this wording.
            items: this.byoAckItems,
            acknowledgements: this.draft.acknowledgements,
            readOnly: this.committed,
          };
        case 'number':
          return {
            // NO priceQuote (2026-09-02). my-status stopped returning one — Teamio pays for the
            // number, so there is no price to hand down and no screen that would render it.
            setupUnavailable: this.numberSetupUnavailable,
            // One Teamio number per site (2026-09-27): a site that already has one sees a contact-us
            // panel instead of the picker. Same my-status field the Connect screen reads.
            existingNumber: (this.status && this.status.whatsappNumber) || '',
            localAvailable: !!(this.status && this.status.countryGate && this.status.countryGate.localAvailable),
            busy: this.busy,
            // The picker (2026-09-11). No price in either list — `my-number-options` returns none.
            recommended: this.numberOptions.recommended,
            searchResult: this.numberOptions.search,
            loadingOptions: this.numberOptionsLoading,
            searching: this.numberSearching,
          };
        case 'handoff':
          return {
            variant: this.handoffVariant,
            byoNumber: this.triage.byoNumber || '',
            canBuyUsNumber: this.canBuyUsNumber,
            busy: this.busy,
          };
        case 'connect':
          return {
            // The number to type into Facebook, named on screen (2026-09-11). From my-status — the
            // site's GENERAL_PHONE — never from anything the practice typed in triage: a practice
            // keeping its app number recorded THAT number there, and it is exactly the wrong one.
            whatsappNumber: (this.status && this.status.whatsappNumber) || '',
            displayName: this.triage.displayName || '',
            facebookAccess: this.triage.facebookAccess || '',
            sdkReady: this.sdkReady,
            connectLink: this.connectLink,
            busy: this.busy,
          };
        case 'progress':
          return {
            milestones: this.status ? milestoneStates(this.status.step, this.status.enabled) : [],
            progressLine: this.status ? statusLine(this.status.step, this.status.hasIssue) : '',
          };
        default:
          return {};
      }
    },
  },

  watch: {
    /**
     * The server is the authority, so a new status resets local navigation. Without this, the
     * practice submits triage, the status comes back saying "you are on the number screen", and the
     * wizard would keep showing them the confirm screen they were on when they clicked.
     */
    status: {
      immediate: true,
      handler() {
        this.viewStep = null;
        // A fresh answer from the server supersedes the last refusal — otherwise a Refresh that
        // fixed the problem would still show "we can't set up a number right now".
        this.numberSetupUnavailable = false;
        this.syncDraftFromStatus();
        // A Refresh on the number screen retries a recommendation that failed to load.
        this.ensureNumberOptions();
      },
    },

    /** The number screen's recommendation loads when the screen comes up (2026-09-11). */
    currentKey: {
      immediate: true,
      handler() {
        this.ensureNumberOptions();
      },
    },

    /**
     * The practice's name comes from a store getter hydrated on app init, so it is usually there
     * before the first status arrives — but "usually" is how a prefill quietly stops happening on
     * a cold deep-link. Re-running the seed when it lands costs nothing and only ever fills a
     * BLANK field, so it can never overwrite something the practice typed.
     */
    siteName: {
      immediate: true,
      handler() {
        this.syncDraftFromStatus();
      },
    },
  },

  methods: {
    /**
     * Reconcile the local draft with the server.
     *
     * Once triage is committed the server's echo REPLACES the draft outright — what is on screen
     * has to be what was actually saved, not what someone typed afterwards. Before that, local
     * edits are preserved (a status refresh must not wipe a half-typed name) and only a blank
     * display name is prefilled from the practice's name on the account.
     */
    syncDraftFromStatus() {
      const triage = this.status && this.status.triage;
      if (triage) {
        this.draft = {
          ...emptyDraft(),
          hasWhatsAppNumber: triage.hasWhatsAppNumber === true,
          numberOnApp: triage.numberOnApp,
          // A blob saved before this answer existed has no key at all — that reads as null (a move).
          keepsNumberOnApp: typeof triage.keepsNumberOnApp === 'boolean' ? triage.keepsNumberOnApp : null,
          wantsLocalNumber: triage.wantsLocalNumber,
          byoNumber: triage.byoNumber,
          facebookAccess: triage.facebookAccess || null,
          displayName: triage.displayName || '',
          consentAcknowledged: triage.consentAcknowledged === true,
          // Echoed back so the acknowledgement screen renders ticked when walked back through as a
          // read-only review. The server stores each ack key at the top level of the triage blob.
          acknowledgements: this.acknowledgementsFromTriage(triage),
        };
        return;
      }
      if (!this.draft.displayName && this.siteName) {
        this.draft = { ...this.draft, displayName: this.siteName };
      }
    },

    /** Reconstruct the acknowledgement map from the echoed committed triage, using the server keys. */
    acknowledgementsFromTriage(triage) {
      const acks = {};
      this.byoAckKeys.forEach((key) => {
        if (triage[key] === true) acks[key] = true;
      });
      return acks;
    },

    onUpdate(patch) {
      if (this.committed) return;
      this.draft = { ...this.draft, ...patch };
    },

    onBack() {
      const prev = previousWizardStep(this.currentKey, this.draft);
      if (!prev) return;
      this.errorMessage = '';
      this.viewStep = prev;
    },

    /**
     * A screen asking to show a DIFFERENT screen. Pure local navigation — no request, ever.
     *
     * This is how the practice moves between the two number branches: the number screen offers "I'd
     * rather have a local number" and the handoff screen offers "get a US number now instead". Both
     * are the same one backend state (`triage`), so nothing is submitted by changing which of them
     * is on screen, and nothing is decided until they click something on the screen they land on.
     *
     * The allow-list is not ceremony: `viewStep` overrides the server's own answer for as long as
     * it is set, so a screen that could point it anywhere could park a practice on a screen their
     * state does not support — and every button there would fail.
     */
    onNavigate(key) {
      if (key !== 'number' && key !== 'handoff') return;
      this.errorMessage = '';
      this.viewStep = key;
    },

    onContinue() {
      if (this.busy || !this.canContinue) return;
      const key = this.currentKey;

      // The tab owns the Meta SDK and the whole Embedded Signup handshake.
      if (key === 'connect') {
        this.$emit('connect');
        return;
      }

      if (key === 'confirm') {
        this.continueFromConfirm();
        return;
      }

      const next = nextReviewStep(key, this.draft);
      if (next) this.viewStep = next;
    },

    /**
     * ⚠️ THE ONE RULE THIS METHOD EXISTS FOR: never re-POST my-triage.
     *
     * The endpoint takes exactly one submission per site (`step === 'not_started'`, else 409
     * `onboarding_in_progress`). A practice that goes Back to check an answer and presses Continue
     * again is REVIEWING, not resubmitting — so when the server has already echoed the answers
     * back, this just drops local navigation and lets the server state render. Sending the request
     * anyway would put a 409 on screen for a perfectly ordinary click.
     */
    continueFromConfirm() {
      if (this.committed) {
        this.viewStep = null;
        return;
      }
      this.submitTriage();
    },

    async submitTriage() {
      if (this.saving) return;
      this.saving = true;
      this.errorMessage = '';
      try {
        const idToken = await getIdToken(this.$store.state.getAuth());
        // The acknowledgements ride along in THIS one POST, keyed by the server's own item.key, and
        // only on the BYO path — off it the backend records them as null, so we send none and never
        // manufacture a stale "true" for a practice that is not moving an existing number.
        const acknowledgements = requiresByoAck(this.draft) ? this.acknowledgementsForSubmit : {};
        await axios.post('/wa-onboarding/my-triage', {
          siteID: this.siteId,
          idToken,
          // Exactly the backend's answer contract, normalized here as well as there: booleans are
          // real booleans and the name is trimmed, so what the practice sees is what is stored.
          answers: {
            hasWhatsAppNumber: this.draft.hasWhatsAppNumber === true,
            numberOnApp: this.draft.numberOnApp,
            keepsNumberOnApp: this.draft.keepsNumberOnApp,
            wantsLocalNumber: this.draft.wantsLocalNumber,
            byoNumber: this.draft.byoNumber,
            facebookAccess: this.draft.facebookAccess,
            displayName: (this.draft.displayName || '').trim(),
            consentAcknowledged: this.draft.consentAcknowledged === true,
            ...acknowledgements,
          },
        });
        this.viewStep = null;
        this.$emit('refresh');
      } catch (e) {
        console.error('WA wizard: my-triage failed:', e);
        this.handleWizardFailure(e);
      } finally {
        this.saving = false;
      }
    },

    /**
     * THE ONE ENDPOINT IN THIS WIZARD THAT SPENDS MONEY — Teamio's money, since 2026-09-02.
     *
     * Called only from WizardNumberStep's `purchase` event, which is emitted only after its confirm
     * dialog has resolved true.
     *
     * ⚠️ THE BODY IS `{ siteID, idToken, phoneNumber }` AND NOTHING ELSE. There is no `acceptedPrice`
     * any more: the practice is shown no price (Teamio absorbs the number cost), so there is nothing
     * for it to accept, and the server does its own internal price check against its own quote. Do
     * not add a price field back here — the endpoint ignores one, and sending it would re-create the
     * impression that the browser has a say in what is spent.
     *
     * ⚠️ NO AUTOMATIC RETRY, EVER. A refusal means the server's view of this site has moved on;
     * `shouldRefreshStatus` decides which codes re-read status, and the practice decides again on
     * what is then true. Every other failure leaves them where they were with the button live again.
     */
    async submitPurchase({ phoneNumber } = {}) {
      if (this.saving) return;
      // The one thing the server refuses outright, so a malformed emit cannot reach the endpoint.
      if (!phoneNumber) return;
      this.saving = true;
      this.errorMessage = '';
      // A new attempt supersedes the previous refusal — the panel must not outlive it.
      this.numberSetupUnavailable = false;
      try {
        const idToken = await getIdToken(this.$store.state.getAuth());
        await axios.post('/wa-onboarding/my-purchase-number', {
          siteID: this.siteId,
          idToken,
          phoneNumber,
        });
        // The server is now at `number_ready`, so the re-read moves the practice to the connect
        // screen. Dropping viewStep first is what lets it: a stale local view outranks the server.
        this.viewStep = null;
        this.$emit('refresh');
      } catch (e) {
        console.error('WA wizard: my-purchase-number failed:', e);
        this.handleWizardFailure(e);
      } finally {
        this.saving = false;
      }
    },

    /**
     * The number screen's lists (2026-09-11). READ-ONLY lookups — `my-number-options` writes nothing
     * and spends nothing — so they deliberately do NOT take the `saving` flag: a list loading in the
     * background must not freeze Back and Refresh. Only the purchase does that.
     *
     * Load the recommendation when the number screen is up and it is not loaded yet (or last failed).
     */
    ensureNumberOptions() {
      if (this.currentKey !== 'number') return;
      // A site that already has a Teamio number is shown no picker (2026-09-27), so don't search for one.
      if (this.status && String(this.status.whatsappNumber || '').trim() !== '') return;
      const rec = this.numberOptions.recommended;
      if (this.numberOptionsLoading || (rec && !rec.failed)) return;
      this.loadNumberOptions();
    },

    /** The ONE call to my-number-options: the recommendation (no area code) or a search (one). */
    async fetchNumberOptions(areaCode) {
      const idToken = await getIdToken(this.$store.state.getAuth());
      const body = areaCode ? { siteID: this.siteId, idToken, areaCode } : { siteID: this.siteId, idToken };
      const res = await axios.post('/wa-onboarding/my-number-options', body);
      return res.data;
    },

    async loadNumberOptions() {
      this.numberOptionsLoading = true;
      try {
        const recommended = await this.fetchNumberOptions();
        this.numberOptions = { ...this.numberOptions, recommended };
      } catch (e) {
        console.error('WA wizard: my-number-options (recommendation) failed:', e);
        // `failed` lets the screen say so (and a Refresh retry) instead of claiming "no suggestion".
        this.numberOptions = {
          ...this.numberOptions,
          recommended: { basis: 'none', areaCode: null, region: null, numbers: [], failed: true },
        };
        this.handleLookupFailure(e);
      } finally {
        this.numberOptionsLoading = false;
      }
    },

    /** The practice's own area-code search. `areaCode` arrives validated from AreaCodeSearch. */
    async searchNumbers(areaCode) {
      if (!areaCode || this.numberSearching) return;
      this.numberSearching = true;
      this.errorMessage = '';
      try {
        const search = await this.fetchNumberOptions(areaCode);
        this.lastNumberSearch = areaCode;
        this.numberOptions = { ...this.numberOptions, search };
      } catch (e) {
        console.error('WA wizard: my-number-options (search) failed:', e);
        this.handleLookupFailure(e);
      } finally {
        this.numberSearching = false;
      }
    },

    /**
     * After `number_taken`: the list the practice picked from is stale. Re-read the recommendation
     * and re-run their last search, so what they pick next is a number that exists.
     */
    refreshNumberOptions() {
      this.numberOptions = { ...this.numberOptions, recommended: null };
      this.loadNumberOptions();
      if (this.lastNumberSearch) this.searchNumbers(this.lastNumberSearch);
    },

    /** A failed lookup: the banner, and a status re-read for the refusals that change the screen. */
    handleLookupFailure(err) {
      const parsed = parseWizardError(err);
      this.errorMessage = parsed.message;
      if (shouldRefreshStatus(parsed)) this.$emit('refresh');
    },

    /**
     * "Teamio takes it from here" — the structured exit for the branches phase 1 does not automate.
     *
     * `reason` comes from the handoff screen's variant, not from anything typed, because it is what
     * our operator channel is paged with: it decides whether someone goes to move an existing
     * number or to source a local one. The backend refuses anything off its own list with
     * `invalid_reason`, which is the banner the practice would see.
     */
    async submitHandoff(reason) {
      if (this.saving) return;
      if (!reason) return;
      this.saving = true;
      this.errorMessage = '';
      try {
        const idToken = await getIdToken(this.$store.state.getAuth());
        await axios.post('/wa-onboarding/my-handoff', {
          siteID: this.siteId,
          idToken,
          reason,
        });
        // The site is now at `number_pending`, which derives to the handoff_pending ending.
        this.viewStep = null;
        this.$emit('refresh');
      } catch (e) {
        console.error('WA wizard: my-handoff failed:', e);
        this.handleWizardFailure(e);
      } finally {
        this.saving = false;
      }
    },

    /**
     * Mint the public sign-in link, for the practice whose Facebook account someone else looks after.
     *
     * Nothing on the wizard's own state depends on the result — the link is handed to a person and
     * used elsewhere — so no refresh is emitted: `my-connect-init` moves the site from `number_ready`
     * to `link_sent`, and BOTH derive to this same connect screen, so re-reading would only replace
     * the screen with itself.
     *
     * The 409 is special-cased because that path does not use the wizard's error contract: it
     * answers `createErrorResponse(409, "…")`, whose body is a bare JSON string with no `code` and
     * no `message` field, so `parseWizardError` can only offer the generic sentence. The status
     * code is the whole signal there, which is exactly how WhatsAppTab's own connect reads it.
     */
    async requestConnectLink() {
      if (this.saving) return;
      this.saving = true;
      this.errorMessage = '';
      try {
        const idToken = await getIdToken(this.$store.state.getAuth());
        const res = await axios.post('/wa-onboarding/my-connect-init', {
          siteID: this.siteId,
          idToken,
        });
        this.connectLink = connectLinkUrl(window.location.origin, res.data && res.data.token);
        if (!this.connectLink) {
          this.errorMessage = 'We could not create a sign-in link just now. Please try again.';
        }
      } catch (e) {
        console.error('WA wizard: my-connect-init failed:', e);
        this.errorMessage = e.response && e.response.status === 409
          ? 'WhatsApp setup has already moved on for your practice. Refresh this page to see where '
            + 'it is now.'
          : parseWizardError(e).message;
      } finally {
        this.saving = false;
      }
    },

    /**
     * One place that decides what a failed wizard request puts on screen.
     *
     * Some refusals CHANGE what this practice should be looking at rather than asking them to try
     * again — an embargoed country, a site that had already started, a step that has since
     * advanced. For those the banner alone would float over a screen that can never succeed, so
     * status is re-read and the wizard re-renders from what the server now says.
     * `shouldRefreshStatus` owns that list, next to the error contract it is derived from.
     *
     * NUMBER_SETUP_UNAVAILABLE_CODES is the other half: those two mean "we could not provision a
     * number just now", which is a fact about the SCREEN, not about the request. Recording it is
     * what lets the number step swap its form for an explanation — since 2026-09-02 that screen has
     * no price quote to infer the same thing from, because there is no price on the wire at all.
     */
    handleWizardFailure(err) {
      const parsed = parseWizardError(err);
      this.errorMessage = parsed.message;
      if (NUMBER_SETUP_UNAVAILABLE_CODES.includes(parsed.code)) this.numberSetupUnavailable = true;
      // The picked number was taken between the list and the click (2026-09-11): nothing was bought,
      // so the lists are re-read and the practice picks again.
      if (NUMBER_TAKEN_CODES.includes(parsed.code)) this.refreshNumberOptions();
      if (shouldRefreshStatus(parsed)) this.$emit('refresh');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../PatientPayments/tokens';

.whatsapp-wizard {
  max-width: 960px;
}

.waw-alert {
  font-size: 0.88rem;
}

.waw-rail {
  display: flex;
  align-items: center;
  gap: 1.4rem;
  margin: 0 0 1rem;
  padding: 0 0.15rem;
  flex-wrap: wrap;
}

.waw-rail__item {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  color: $pp-text-subtle;
}

.waw-rail__dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  background: $pp-surface;
  border: 2px solid $pp-border;
  color: $pp-text-subtle;
}

.waw-rail__item.is-done .waw-rail__dot {
  background: #25d366; /* WhatsApp's own green — it identifies the channel. */
  border-color: #25d366;
  color: #fff;
}

.waw-rail__item.is-current .waw-rail__dot {
  background: $pp-brand;
  border-color: $pp-brand;
  color: #fff;
}

.waw-rail__item.is-done .waw-rail__label,
.waw-rail__item.is-current .waw-rail__label {
  color: $pp-text-strong;
  font-weight: 600;
}

@media (max-width: 575.98px) {
  .waw-rail {
    gap: 0.75rem;
  }
}
</style>
